// File: src/components/modals/HelpModal.vue
<template>
    <ModalComponent
        :isVisible="isVisible"
        @close="$emit('close')"
        :width="'50%'"
        aria-labelledby="help-modal-title"
    >
        <template #header>
            <h2 id="help-modal-title">{{ title }}</h2>
        </template>
        <template #body>
            <div v-if="isLoading" class="text-center p-4">
                <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>
                {{ $t('common.loading') }}
            </div>
            <div v-else-if="error" class="alert alert-danger">
                <strong>{{ $t('common.error') }}:</strong> {{ error }}
            </div>
            <div v-else class="help-content" v-html="sanitizedContent"></div>
        </template>
        <template #footer>
            <button class="btn btn-secondary" @click="$emit('close')">
                {{ $t('common.close') }}
            </button>
        </template>
    </ModalComponent>
</template>

<script setup>
/**
 * HelpModal Component
 *
 * Displays help content in a modal dialog with automatic HTML sanitization.
 * Used throughout the application to show contextual help from i18n HTML files.
 *
 * Security: All HTML content is automatically sanitized using sanitizeHTML()
 * to prevent XSS attacks while preserving safe formatting and links.
 *
 * Usage Examples:
 * - GeoBlocking help: loads from src/i18n/help/geoBlocking.en.html
 * - Domain management help: loads from src/i18n/help/domains.en.html
 * - General component help via useHelpContent composable
 *
 * @example
 * <HelpModal
 *   :isVisible="showHelp"
 *   :title="$t('help.geoBlocking.title')"
 *   :content="helpContent"
 *   @close="showHelp = false"
 * />
 */

import { computed } from 'vue'
import ModalComponent from '@/components/ModalComponent.vue'
import { sanitizeHTML } from '@/utils/sanitization'

const props = defineProps({
    isVisible: Boolean,
    title: String,
    content: String,
    isLoading: {
        type: Boolean,
        default: false
    },
    error: {
        type: String,
        default: null
    }
})

/**
 * Sanitized help content ready for v-html rendering
 *
 * Automatically sanitizes the raw HTML content to prevent XSS attacks
 * while preserving safe HTML formatting, links, and structure.
 *
 * This is critical for security when rendering help content loaded
 * from i18n HTML files or any external source.
 */
const sanitizedContent = computed(() => {
    return sanitizeHTML(props.content)
})
</script>
<style scoped lang="scss">
.help-content {
    padding-right: 1rem;
}
</style>
